import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Linkedin, Mail } from "lucide-react";
import { DemoScheduleForm } from "./enterprise/DemoScheduleForm";
import { ContactSalesForm } from "./enterprise/ContactSalesForm";
import { useState } from "react";

export const Footer = () => {
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  
  return (
    <footer className="relative overflow-hidden" style={{ backgroundColor: 'hsl(var(--beige))' }}>
      {/* Neural Network Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{ 
          background: 'var(--gradient-depth)',
          opacity: 0.8 
        }} />
        {/* Animated Neural Connections */}
        <div className="absolute inset-0">
          <svg className="w-full h-full opacity-20" viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid slice">
            <defs>
              <radialGradient id="nodeGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.2" />
              </radialGradient>
              <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                <stop offset="50%" stopColor="hsl(var(--accent))" stopOpacity="0.6" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            
            {/* Neural Network Connections */}
            <g className="animate-pulse" style={{ animationDuration: '4s' }}>
              <line x1="100" y1="150" x2="300" y2="250" stroke="url(#connectionGradient)" strokeWidth="1" />
              <line x1="300" y1="250" x2="500" y2="180" stroke="url(#connectionGradient)" strokeWidth="1" />
              <line x1="500" y1="180" x2="700" y2="300" stroke="url(#connectionGradient)" strokeWidth="1" />
              <line x1="200" y1="400" x2="400" y2="350" stroke="url(#connectionGradient)" strokeWidth="1" />
              <line x1="400" y1="350" x2="600" y2="450" stroke="url(#connectionGradient)" strokeWidth="1" />
              <line x1="150" y1="600" x2="350" y2="550" stroke="url(#connectionGradient)" strokeWidth="1" />
              <line x1="350" y1="550" x2="550" y2="620" stroke="url(#connectionGradient)" strokeWidth="1" />
            </g>
            
            {/* Neural Nodes */}
            <g>
              <circle cx="100" cy="150" r="4" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '0s', animationDuration: '3s' }} />
              <circle cx="300" cy="250" r="6" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '1s', animationDuration: '3s' }} />
              <circle cx="500" cy="180" r="4" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '2s', animationDuration: '3s' }} />
              <circle cx="700" cy="300" r="5" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '0.5s', animationDuration: '3s' }} />
              <circle cx="200" cy="400" r="4" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '1.5s', animationDuration: '3s' }} />
              <circle cx="400" cy="350" r="6" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '2.5s', animationDuration: '3s' }} />
              <circle cx="600" cy="450" r="4" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '0.8s', animationDuration: '3s' }} />
              <circle cx="150" cy="600" r="5" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '1.8s', animationDuration: '3s' }} />
              <circle cx="350" cy="550" r="4" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '2.2s', animationDuration: '3s' }} />
              <circle cx="550" cy="620" r="6" fill="url(#nodeGradient)" className="animate-pulse" style={{ animationDelay: '0.3s', animationDuration: '3s' }} />
            </g>
          </svg>
        </div>
        {/* Floating Particles */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-accent/50 rounded-full animate-bounce" style={{ animationDelay: '0s', animationDuration: '6s' }} />
          <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '2s', animationDuration: '8s' }} />
          <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-accent/40 rounded-full animate-bounce" style={{ animationDelay: '4s', animationDuration: '10s' }} />
          <div className="absolute bottom-1/3 right-1/3 w-2 h-2 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: '1s', animationDuration: '7s' }} />
        </div>
      </div>

      {/* Glass Morphism Container */}
      <div className="relative z-10 glass-card border-0 border-t border-white/10" style={{
        background: 'linear-gradient(135deg, hsl(var(--background) / 0.8), hsl(var(--muted) / 0.6))',
        backdropFilter: 'blur(20px)',
        boxShadow: 'var(--shadow-neural)'
      }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          {/* Main Content - Mobile-first horizontal layout */}
          <div className="space-y-12">
            
            {/* Brand & Description Section */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 lg:gap-12">
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-4">
                  <Link to="/" className="inline-block group">
                    <img
                      src="/mcai_logo_green.svg"
                      alt="Scienza Health Logo"
                      className="h-10 w-auto transition-transform duration-200 group-hover:scale-105"
                    />
                  </Link>
                  <Badge variant="secondary" className="bg-muted text-foreground border border-border">
                    FDA Registered Device
                  </Badge>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
                  AI-powered cognitive health solutions for healthcare professionals
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setShowDemoDialog(true)}
                  size="lg"
                  className="transition-all duration-200 hover:scale-105"
                >
                  Book Demo
                </Button>
                <Button 
                  onClick={() => setShowContactDialog(true)}
                  variant="outline" 
                  size="lg"
                  className="transition-all duration-200 hover:scale-105"
                >
                  Get Help
                </Button>
              </div>
            </div>

            {/* Navigation Links - Aligned with main navigation */}
            <div className="flex flex-col md:flex-row justify-between gap-12 md:gap-8">
              
              {/* Solutions Section */}
              <div className="space-y-4 group">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                    Solutions
                  </h3>
                  <nav className="flex flex-col sm:flex-row md:flex-col gap-4 sm:gap-6 md:gap-4">
                    <Link 
                      to="/solutions/clinical-automation" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Clinical Automation
                    </Link>
                    <Link 
                      to="/solutions/workforce-customer-experience" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Workforce & Customer Experience
                    </Link>
                  </nav>
                </div>
              </div>

              {/* Platform & Devices Section */}
              <div className="space-y-4 group">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                    Platform & Devices
                  </h3>
                  <nav className="flex flex-col sm:flex-row md:flex-col gap-4 sm:gap-6 md:gap-4">
                    <Link 
                      to="/platform-devices/ai-enabled-bedside-devices" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      AI-Enabled Bedside Devices
                    </Link>
                    <Link 
                      to="/platform-devices/wearables" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Wearables
                    </Link>
                    <Link 
                      to="/platform-devices/ehr-workflow-integrations" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      EHR Workflow Integrations
                    </Link>
                  </nav>
                </div>
              </div>

              {/* Resources Section */}
              <div className="space-y-4 group">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
                    Resources
                  </h3>
                  <nav className="flex flex-col sm:flex-row md:flex-col gap-4 sm:gap-6 md:gap-4">
                    <Link 
                      to="/resources/case-studies" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Case Studies
                    </Link>
                    <Link 
                      to="/resources/white-papers-research" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      White Papers & Research
                    </Link>
                    <Link 
                      to="/resources/webinars-demos" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Webinars & Demos
                    </Link>
                    <Link 
                      to="/resources/blog-news" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Blog & News
                    </Link>
                  </nav>
                </div>
              </div>

              {/* About Section */}
              <div className="space-y-4 group">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '1.5s' }} />
                    About
                  </h3>
                  <nav className="flex flex-col sm:flex-row md:flex-col gap-4 sm:gap-6 md:gap-4">
                    <Link 
                      to="/about" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      About Us
                    </Link>
                    <Link 
                      to="/technology" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Technology
                    </Link>
                    <Link 
                      to="/how-it-works" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      How It Works
                    </Link>
                    <Link 
                      to="/compliance-security" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Compliance & Security
                    </Link>
                    <Link 
                      to="/contact-request-demo" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Contact
                    </Link>
                  </nav>
                </div>
              </div>

              {/* Legal Section */}
              <div className="space-y-4 group">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
                    Legal
                  </h3>
                  <nav className="flex flex-col sm:flex-row md:flex-col gap-4 sm:gap-6 md:gap-4">
                    <Link 
                      to="/legal/privacy-policy" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Privacy Policy
                    </Link>
                    <Link 
                      to="/legal/terms-of-service" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Terms of Service
                    </Link>
                    <Link 
                      to="/accessibility" 
                      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:translate-x-1 transform"
                    >
                      Accessibility
                    </Link>
                  </nav>
                </div>
              </div>

              {/* Social Links */}
              <div className="space-y-4 group">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '2.5s' }} />
                    Connect
                  </h3>
                  <div className="flex gap-4">
                    <a
                      href="https://linkedin.com/company/memorycareai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-muted hover:bg-accent transition-all duration-200 hover:scale-110 group"
                      aria-label="Connect with us on LinkedIn"
                    >
                      <Linkedin className="h-5 w-5 text-muted-foreground group-hover:text-accent-foreground transition-colors" />
                    </a>
                    <a
                      href="mailto:contact@scienzahealth.com"
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-muted hover:bg-accent transition-all duration-200 hover:scale-110 group"
                      aria-label="Contact us via email"
                    >
                      <Mail className="h-5 w-5 text-muted-foreground group-hover:text-accent-foreground transition-colors" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="pt-8 border-t border-white/10">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <p className="text-sm text-muted-foreground text-center md:text-left">
                  © 2025 Scienza Health. All rights reserved. Always consult with qualified healthcare professionals for medical diagnosis and treatment.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Powered by</span>
                  <span className="text-sm font-medium text-accent">Digital Human® Technology</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog Components */}
      <DemoScheduleForm 
        open={showDemoDialog} 
        onOpenChange={setShowDemoDialog}
      />
      <ContactSalesForm 
        open={showContactDialog} 
        onOpenChange={setShowContactDialog}
      />
    </footer>
  );
};